import { ArticleJsonLd, NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Container } from 'src/components/container'
import { WafFriendlyPass } from 'src/types/waf-friendly-pass'
import { Meta } from 'src/utils/meta'
import { wpApiUrl } from 'src/utils/wpApiUrl'
import Image from 'next/image'
import styles from './styles.module.scss'
import { FeatureTag } from 'src/components/featureTag'
import { GetServerSideProps } from 'next'
import { Routes } from 'src/utils/routes'
import Link from 'next/link'
import { IconExpandRight } from 'src/components/icon/expandRight'
import { IconTargetBlank } from 'src/components/icon/targetBlank'
import { Map } from 'src/components/googleMap'
import Device from 'src/components/device'
import { Notice } from 'src/components/notice/notice'
import { NoticeHeading } from 'src/components/notice/noticeHeading'
import { NoticeSmall } from 'src/components/notice/noticeSmall'
import { NoticeText } from 'src/components/notice/noticeText'
import { SideImageBanner } from 'src/components/sideImageBanner'
import { Breadcrumbs } from 'src/components/breadcrumb'
import { fetchWithCache } from 'src/utils/fetchWithCache'
import { SkeletonLoaderPage } from 'src/components/skeleton/page'
import dayjs from 'dayjs'
import { formatDate } from 'src/utils/dayjs/formatDate'
import { useIsAuthenticated } from 'src/store/userUid'
import { CpBannerSquare } from 'src/components/cpBanner/202309_traffic_square/'

type Props = {
  serviceItem: WafFriendlyPass.service
  spotItem: WafFriendlyPass.spot
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = context?.params?.serviceId
  const serviceItem = await fetchWithCache(`${wpApiUrl.service}/${id}`)
  return { props: { serviceItem } }
}

const ServiceIDPage = ({ serviceItem, spotItem }: Props) => {
  const [loadOnce, setLoadOnce] = useState(false)
  const router = useRouter()
  const { serviceId } = router.query

  const [service, setService] = useState<WafFriendlyPass.service>()
  const [spot, setSpot] = useState<WafFriendlyPass.spot>()

  const firstLoad = async () => {
    const spotItem = (await fetchWithCache(
      `${wpApiUrl.spot}/${serviceItem?.acf.spot[0]}?_fields=id,acf`
    )) as Props['spotItem']
    setService(serviceItem)
    setSpot(spotItem)
  }

  useEffect(() => {
    if (!serviceId || loadOnce) {
      return
    }
    firstLoad()
    setTimeout(() => {
      setLoadOnce(true)
    }, 800)
  }, [])

  const { isAuthenticated } = useIsAuthenticated()
  useEffect(() => {
    isAuthenticated
  }, [isAuthenticated])

  const breadcrumbs = [
    { name: 'トップ', href: Routes.HOME },
    { name: '特典', href: '/service/page/1' },
    { name: `${service?.acf.name}` },
  ]

  return (
    <>
      <NextSeo
        title={`${serviceItem ? serviceItem.acf.name : Meta.TITLE} | ${
          Meta.TITLE
        }`}
        description={serviceItem.acf.desc}
        openGraph={{
          url: `https://pass.workation-fukuoka.jp${router.asPath}`,
          title: `${serviceItem.acf.name} | ${Meta.TITLE}`,
          description: `${serviceItem.acf.desc}`,
          type: 'article',
          images: [
            {
              url: `${
                serviceItem?.acf.image
                  ? serviceItem?.acf.image.url
                  : Meta.OGP_IMAGE
              }`,
              width: 1200,
              height: 630,
              alt: `${serviceItem?.acf.name} | ${Meta.TITLE}`,
            },
          ],
        }}
      />
      <ArticleJsonLd
        url={`https://pass.workation-fukuoka.jp${router.asPath}`}
        title={`${serviceItem.acf.name} | ${Meta.TITLE}`}
        images={
          serviceItem.acf.image ? serviceItem.acf.image.url : Meta.OGP_IMAGE
        }
        datePublished={formatDate(dayjs(serviceItem?.date).toDate())}
        dateModified={formatDate(dayjs(serviceItem?.modified).toDate())}
        authorName="W@F事務局"
        description={serviceItem.acf.desc}
      />
      <Container>
        {service && loadOnce ? (
          <div className={styles.service}>
            {service?.acf.image && (
              <div className={styles.serviceImage}>
                <Image
                  src={service.acf.image.url}
                  layout="responsive"
                  width={960}
                  height={450}
                  priority={true}
                  alt={service.acf.image.title}
                />
              </div>
            )}
            <div className={styles.serviceBody}>
              <div className={styles.serviceValue}>
                <div className={styles.serviceValueTax}>
                  <p>{spot?.acf.taxonomy.type.name}</p>
                  <span>/</span>
                  <p>{spot?.acf.taxonomy.area.name}</p>
                </div>
                <h1 className={styles.serviceValueHeading}>
                  {service?.acf.name}
                </h1>
                <Device>
                  {({ isMobile }) => {
                    if (isMobile) {
                      return (
                        <div className="utilsSp">
                          <p className={styles.serviceSidePriceLabel}>
                            「ワフパス特典」利用価格
                          </p>
                          <p className={styles.serviceSidePrice}>
                            <b>{service?.acf.price}</b>
                          </p>
                          <p className={styles.serviceSideInfo}>
                            施設にて専用二次元バーコードを読み込むとご利用いただけます。
                          </p>

                          <div>
                            <Link href={`${Routes.MY_PAGE}`}>
                              <a className={`${styles.serviceUseButton} }`}>
                                {isAuthenticated
                                  ? 'この特典を利用する'
                                  : '会員登録・ログインして特典を利用する'}
                              </a>
                            </Link>
                          </div>
                        </div>
                      )
                    }
                  }}
                </Device>
                {service?.acf.taxonomy.feature && (
                  <div className={styles.serviceValueFeature}>
                    {service?.acf.taxonomy.feature.map(
                      (item: WafFriendlyPass.taxonomy, idx: number) => {
                        return <FeatureTag key={idx} text={item.name} />
                      }
                    )}
                  </div>
                )}
                <div
                  className={styles.serviceValueDesc}
                  dangerouslySetInnerHTML={{ __html: service?.acf.desc }}
                />

                {service?.acf.referenceLink &&
                  service?.acf.referenceLink[0].url && (
                    <>
                      <p className={styles.serviceValueRefLinkLabel}>
                        関連リンク(申し込み専用URL等)
                      </p>
                      {isAuthenticated ? (
                        <div className={styles.serviceValueRefLink}>
                          {service?.acf.referenceLink.map(
                            (
                              item: { title: string; url: string },
                              idx: number
                            ) => {
                              return (
                                <a
                                  href={item.url}
                                  className={styles.serviceValueRefLinkItem}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  key={idx}
                                >
                                  <p
                                    className={
                                      styles.serviceValueRefLinkItemTitle
                                    }
                                  >
                                    {item.title}
                                  </p>
                                  <span
                                    className={
                                      styles.serviceValueRefLinkItemIcon
                                    }
                                  >
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                    >
                                      <path
                                        d="M4.66667 2V3.33333H1.33333V10.6667H8.66667V7.33333H10V11.3333C10 11.5101 9.92976 11.6797 9.80474 11.8047C9.67971 11.9298 9.51014 12 9.33333 12H0.666667C0.489856 12 0.320286 11.9298 0.195262 11.8047C0.0702379 11.6797 0 11.5101 0 11.3333V2.66667C0 2.48986 0.0702379 2.32029 0.195262 2.19526C0.320286 2.07024 0.489856 2 0.666667 2H4.66667ZM12 0V5.33333H10.6667V2.27533L5.47133 7.47133L4.52867 6.52867L9.72333 1.33333H6.66667V0H12Z"
                                        fill="#fff"
                                      />
                                    </svg>
                                  </span>
                                </a>
                              )
                            }
                          )}
                        </div>
                      ) : (
                        <Link href={Routes.SIGN_IN} passHref>
                          <a className={styles.serviceValueRefSignin}>
                            会員登録・ログインして関連リンクを確認する
                          </a>
                        </Link>
                      )}
                    </>
                  )}

                {spot?.acf.reception && (
                  <>
                    <p className={styles.serviceValueReceptionLabel}>
                      受付場所
                    </p>
                    <div
                      className={styles.serviceValueReception}
                      dangerouslySetInnerHTML={{ __html: spot?.acf.reception }}
                    />
                  </>
                )}

                {spot && (
                  <div className={styles.serviceValueSpot}>
                    <h2 className={styles.serviceValueSpotLabel}>
                      この特典を利用できる施設
                    </h2>
                    <Link href={`${Routes.SPOT}/${spot.id}`}>
                      <a className={styles.serviceValueSpotCard}>
                        <div className={styles.serviceValueSpotCardImage}>
                          <Image
                            src={spot.acf.images[0].image.sizes.medium}
                            layout="responsive"
                            width={90}
                            height={90}
                            priority={false}
                            alt={spot.acf.images[0].image.title}
                          />
                        </div>
                        <div>
                          <h3 className={styles.serviceValueSpotCardName}>
                            {spot.acf.name}
                          </h3>
                          <ul className={styles.serviceValueSpotCardTax}>
                            {spot.acf.taxonomy.feature &&
                              spot.acf.taxonomy.feature.map(
                                (
                                  item: WafFriendlyPass.taxonomy,
                                  idx: number
                                ) => {
                                  return <li key={idx}>#{item.name}</li>
                                }
                              )}
                          </ul>
                        </div>
                        <span className={styles.serviceValueSpotCardIcon}>
                          <IconExpandRight />
                        </span>
                      </a>
                    </Link>
                    {spot.acf.url && (
                      <a
                        href={spot.acf.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={styles.serviceValueSpotUrl}
                      >
                        <span>施設の公式HP</span>
                        <span className={styles.serviceValueSpotUrlIcon}>
                          <IconTargetBlank />
                        </span>
                      </a>
                    )}
                    <div className={styles.serviceValueSpotMap}>
                      <Map
                        lat={Number(spot.acf.access.googlemap.lat)}
                        lng={Number(spot.acf.access.googlemap.lng)}
                        name={spot.acf.access.googlemap.name}
                      />
                    </div>
                    <p className={styles.serviceValueSpotMapAddress}>
                      {spot.acf.access.address}
                    </p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${spot.acf.access.address}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={styles.serviceValueSpotMapLink}
                    >
                      <span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M12.2427 11.5766L8 15.8193L3.75734 11.5766C2.91823 10.7375 2.34679 9.66838 2.11529 8.5045C1.88378 7.34061 2.0026 6.13422 2.45673 5.03787C2.91086 3.94151 3.67989 3.00445 4.66659 2.34516C5.65328 1.68588 6.81332 1.33398 8 1.33398C9.18668 1.33398 10.3467 1.68588 11.3334 2.34516C12.3201 3.00445 13.0891 3.94151 13.5433 5.03787C13.9974 6.13422 14.1162 7.34061 13.8847 8.5045C13.6532 9.66838 13.0818 10.7375 12.2427 11.5766V11.5766ZM8 10.0006C8.70724 10.0006 9.38552 9.71964 9.88562 9.21955C10.3857 8.71945 10.6667 8.04117 10.6667 7.33393C10.6667 6.62669 10.3857 5.94841 9.88562 5.44831C9.38552 4.94822 8.70724 4.66726 8 4.66726C7.29276 4.66726 6.61448 4.94822 6.11438 5.44831C5.61429 5.94841 5.33333 6.62669 5.33333 7.33393C5.33333 8.04117 5.61429 8.71945 6.11438 9.21955C6.61448 9.71964 7.29276 10.0006 8 10.0006V10.0006ZM8 8.66726C7.64638 8.66726 7.30724 8.52679 7.05719 8.27674C6.80714 8.02669 6.66667 7.68755 6.66667 7.33393C6.66667 6.98031 6.80714 6.64117 7.05719 6.39112C7.30724 6.14107 7.64638 6.0006 8 6.0006C8.35362 6.0006 8.69276 6.14107 8.94281 6.39112C9.19286 6.64117 9.33333 6.98031 9.33333 7.33393C9.33333 7.68755 9.19286 8.02669 8.94281 8.27674C8.69276 8.52679 8.35362 8.66726 8 8.66726Z"
                            fill="#0751E2"
                          />
                        </svg>
                      </span>
                      <span>GoogleMapで見る</span>
                    </a>
                  </div>
                )}
              </div>

              <aside className={styles.serviceSide}>
                <Device>
                  {({ isBrowser }) => {
                    if (isBrowser) {
                      return (
                        <div className="utilsPc">
                          <p className={styles.serviceSidePriceLabel}>
                            「ワフパス特典」利用価格
                          </p>
                          <p className={styles.serviceSidePrice}>
                            <b>{service?.acf.price}</b>
                          </p>
                          <div className={styles.serviceSideButton}>
                            この特典を利用する
                          </div>
                          <div className={styles.serviceSideNotice}>
                            <Notice>
                              <NoticeHeading heading="注意事項" />
                              <NoticeText>
                                特典のご利用は PC端末からのご利用はできません。
                                スマートフォン端末からご利用いただけます。
                              </NoticeText>
                              <NoticeSmall>
                                ※お持ちのスマートフォン端末から再度アクセスしてください。
                              </NoticeSmall>
                            </Notice>
                          </div>
                        </div>
                      )
                    }
                  }}
                </Device>
                <div className={styles.serviceSideBanner}>
                  <SideImageBanner
                    src="/images/banner_cta_sp.png"
                    to={`${Routes.MY_PAGE}`}
                  />
                  {/* <CpBannerSquare /> */}
                </div>
              </aside>
            </div>
          </div>
        ) : (
          <SkeletonLoaderPage />
        )}
      </Container>

      <Breadcrumbs items={breadcrumbs} />
    </>
  )
}

export default ServiceIDPage
